/** Bounded, in-memory chat transcript. No browser or disk persistence: RBIM
 * prompts may include sensitive data, so they must not be written to
 * localStorage or sessionStorage. Server-side persistence needs authenticated
 * per-user storage and an approved retention policy. */
export const LIRA_TRANSCRIPT_LIMIT = 600; // 300 full request/response pairs
export const LIRA_VISIBLE_MESSAGES = 60;

export function appendLiraChatMessage<T>(messages: readonly T[], next: T): T[] {
  return [...messages, next].slice(-LIRA_TRANSCRIPT_LIMIT);
}

export function visibleLiraChatMessages<T>(messages: readonly T[], count: number): T[] {
  return messages.slice(-Math.max(1, Math.floor(count)));
}

/** Display *semantic topics* only; values and resident data must be refreshed. */
export function liraTopicRecap(topics: readonly string[] | undefined): string {
  return (topics ?? []).slice(-18).map((topic) => topic.toLowerCase().replace(/_/g, " ")).join(", ");
}

/** A visible separator is shown only after at least one hour without a
 * user/assistant interaction in the currently mounted chat. This is a
 * presentation-only timestamp: never persist RBIM chat data in the browser. */
export const LIRA_INACTIVITY_GAP_MS = 60 * 60 * 1_000;

export function liraConversationResumeLabel(previousActivityMs: number | null, returnedAtMs: number): string | null {
  if (previousActivityMs === null || !Number.isFinite(previousActivityMs) || !Number.isFinite(returnedAtMs)) return null;
  const elapsed = returnedAtMs - previousActivityMs;
  if (elapsed < LIRA_INACTIVITY_GAP_MS) return null;
  const hours = Math.floor(elapsed / LIRA_INACTIVITY_GAP_MS);
  const days = Math.floor(elapsed / (24 * LIRA_INACTIVITY_GAP_MS));
  const duration = days > 0 ? `${days} ${days === 1 ? "day" : "days"}` : `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const when = new Intl.DateTimeFormat("en-PH", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Manila",
  }).format(new Date(returnedAtMs));
  return `Conversation resumed ${when} · After ${duration} away`;
}
